import { useCallback, useEffect, useMemo } from 'react';
import { useInboxQuery, useMarkReadMutation, useMarkUnreadMutation, useMeQuery } from '@platform/state/api';
import { todayIso } from '@platform/core/lib/format';
import type { IInboxEntry } from './IInboxEntry';
import type { IInboxState } from './IInboxState';
import { readOverrides, setReadOverrides, useReadOverridesVersion } from './inboxReadStore';
import { normalizeInbox } from './normalizeInbox';

const POLL_MS = 60000;

/**
 * Вхідні користувача (patterns/notifications-inbox): GET /me/inbox раз на хвилину, нормалізація видів і конверта запису, прочитаність —
 * POST /me/inbox/read і /unread (ідентифікатори елементів) плюс миттєві локальні позначки, які прибираються, щойно сервер має той самий стан
 * (сервер попереднього формату не зберігає стан обчислених елементів і «непрочитано» — тоді позначки лишаються). Лічильники як у прототипі
 * v4: «Непрочитані у вхідних» і меню вхідних — за всіма елементами; дзвіночок і меню профілю — лише непрочитані збережені сповіщення.
 */
export function useInbox(): IInboxState {
  const { data: me } = useMeQuery();
  const { data: wire, isLoading } = useInboxQuery({}, { pollingInterval: POLL_MS });
  const [markServer] = useMarkReadMutation();
  const [unmarkServer] = useMarkUnreadMutation();
  const version = useReadOverridesVersion();
  const userId = me?.id;
  const server = useMemo(() => normalizeInbox(wire ?? [], todayIso()), [wire]);
  const entries = useMemo(() => {
    const overrides = userId && version ? readOverrides(userId) : {};
    return server.map((e) => (e.key in overrides ? { ...e, read: overrides[e.key]! } : e));
  }, [server, userId, version]);
  useEffect(() => {
    if (!userId || !version) return;
    const overrides = readOverrides(userId);
    const settled = server.filter((e) => overrides[e.key] === e.read).map((e) => e.key);
    if (settled.length) setReadOverrides(userId, settled, null);
  }, [server, userId, version]);
  const setRead = useCallback((list: readonly IInboxEntry[], read: boolean) => {
    if (!userId || !list.length) return;
    setReadOverrides(userId, list.map((e) => e.key), read);
    const ids = list.map((e) => e.id);
    void (read ? markServer(ids) : unmarkServer(ids));
  }, [userId, markServer, unmarkServer]);
  const markAllRead = useCallback(() => {
    if (!userId) return;
    setReadOverrides(userId, entries.map((e) => e.key), true);
    void markServer(undefined);
  }, [userId, entries, markServer]);
  const unread = entries.filter((e) => !e.read);
  return { entries, unread: unread.length, storedUnread: unread.filter((e) => e.stored).length, loading: isLoading, setRead, markAllRead };
}
